import { List, Stack, Text } from "basis";
import { FC } from "react";
import { EmploymentStatusType } from "./types";

type Props = {
  employmentStatus: EmploymentStatusType;
};

export const ProofOfIncomeDocuments: FC<Props> = ({ employmentStatus }) => {
  switch (employmentStatus) {
    case "FULL_TIME":
    case "PART_TIME": {
      return (
        <Stack gap="6">
          <Payslip />
          <BankStatement />
        </Stack>
      );
    }
    case "CASUAL": {
      return (
        <Stack gap="6">
          <PayslipCasual />
          <BankStatement />
          <GroupCertificate />
        </Stack>
      );
    }
    case "CONTRACTOR_FULL_TIME":
    case "CONTRACTOR_PART_TIME": {
      return (
        <Stack gap="6">
          <Payslip />
          <BankStatement />
        </Stack>
      );
    }
    case "SELF_EMPLOYED": {
      return (
        <Stack gap="6">
          <TaxOfficeNoticeOfAssessment />
          <AccountantLetter />
          <TaxReturn />
          <ProfitAndLossStatement />
        </Stack>
      );
    }
    case "COMP": {
      return (
        <Stack gap="6">
          <Text>
            <strong>Government Benefits</strong>
          </Text>
          <BankStatementComp />
          <CentrelinkLetter />
          <CenterlinkOnline />
          <VeteransAffairs />
          <Text>
            <strong>Workers Compensation</strong>
          </Text>
          <LetterFromInsurer />
        </Stack>
      );
    }
    case "OTHER": {
      return (
        <Stack gap="6">
          <BankStatementOther />
          <TaxOfficeNoticeOfAssessment />
          <AccountantLetter />
          <TaxReturn />
          <SuperannuationStatement />
          <LetterFromSuperannuation />
          <Text>
            <strong>Rental Income</strong>
          </Text>
          <BankStatementOtherRental />
          <RealEstateRental />
          <CurrentRentalAgreement />
        </Stack>
      );
    }
    default: {
      return null;
    }
  }
};

function Payslip() {
  return (
    <Stack gap="2">
      <Text>
        <strong>Payslip(s)</strong>
      </Text>
      <List>
        <List.Item>
          Is a PDF, screenshot or photo of a full computer-generated payslip
        </List.Item>
        <List.Item>Shows your full name</List.Item>
        <List.Item>Your employer's name or ACN/ABN</List.Item>
        <List.Item>
          Most recent payment is dated within the last 31 days
        </List.Item>
        <List.Item>
          Includes minimum 2 months of year-to-date income or supply 2
          consecutive payslips
        </List.Item>
        <List.Item>Shows net/gross income and frequency of pay</List.Item>
      </List>
    </Stack>
  );
}

function PayslipCasual() {
  return (
    <Stack gap="2">
      <Text>
        <strong>Payslip(s)</strong>
      </Text>
      <List>
        <List.Item>
          Is a PDF, screenshot or photo of a full computer-generated payslip
        </List.Item>
        <List.Item>Shows your full name</List.Item>
        <List.Item>Your employer's name or ACN/ABN</List.Item>
        <List.Item>
          Most recent payment is dated within the last 31 days
        </List.Item>
        <List.Item>Includes minimum 2 months of year-to-date income</List.Item>
        <List.Item>Shows net/gross income and frequency of pay</List.Item>
        <List.Item>
          If applying between 1 July and 31 August supply an additional
          payslip/ATO Income Statement from the end of prior financial year
        </List.Item>
      </List>
    </Stack>
  );
}

function BankStatement() {
  return (
    <Stack gap="2">
      <Text>
        <strong>Bank Statement/Online Transaction History</strong>
      </Text>
      <List>
        <List.Item>Shows your full name</List.Item>
        <List.Item>Your BSB, account number & account name </List.Item>
        <List.Item>
          Two months of income payments, most recent income showing within the
          last 31 days
        </List.Item>
        <List.Item>
          Employer’s name or ACN/ABN corresponding to the deposits
        </List.Item>
      </List>
    </Stack>
  );
}
function BankStatementComp() {
  return (
    <Stack gap="2">
      <Text>
        <strong>Bank Statement/Online Transaction History</strong>
      </Text>
      <List>
        <List.Item>Shows your full name</List.Item>
        <List.Item>Your BSB, account number & account name</List.Item>
        <List.Item>
          Two consecutive income payments, most recent income showing within the
          last 31 days
        </List.Item>
        <List.Item>Shows deposit amounts and dates</List.Item>
        <List.Item>
          Shows identifiable income transaction, e.g. Centrelink
        </List.Item>
      </List>
    </Stack>
  );
}

function BankStatementOther() {
  return (
    <Stack gap="2">
      <Text>
        <strong>Bank Statement/Online Transaction History</strong>
      </Text>
      <List>
        <List.Item>Shows your full name</List.Item>
        <List.Item>Your BSB, account number & account name </List.Item>
        <List.Item>
          Three months of income payments, most recent income showing within the
          last 31 days
        </List.Item>
        <List.Item>
          If latest payment is not within 31 days provide with another income
          document
        </List.Item>
      </List>
    </Stack>
  );
}

function BankStatementOtherRental() {
  return (
    <Stack gap="2">
      <Text>
        <strong>Bank Statement/Online Transaction History</strong>
      </Text>
      <List>
        <List.Item>Shows your full name</List.Item>
        <List.Item>Your BSB, account number & account name </List.Item>
        <List.Item>
          Two months of income payments, most recent income showing within the
          last 31 days
        </List.Item>
        <List.Item>
          Tenant or rental agency’s name corresponding to the deposits
        </List.Item>
      </List>
    </Stack>
  );
}

function GroupCertificate() {
  return (
    <Stack gap="2">
      <Text>
        <strong>ATO Income Statement/Group Certificate</strong>
      </Text>
      <List>
        <List.Item>Shows your full name</List.Item>
        <List.Item>Your employer’s name or ACN/ABN</List.Item>
        <List.Item>
          Is for the most recent financial tax year, if applying in the
          following financial year between 1 July and 31 August, and
        </List.Item>
        <List.Item>
          Supplied with a payslip dated within the last 31 days
        </List.Item>
      </List>
    </Stack>
  );
}

function TaxOfficeNoticeOfAssessment() {
  return (
    <Stack gap="2">
      <Text>
        <strong>Tax Office Notice of Assessment</strong>
      </Text>
      <List>
        <List.Item>Shows your full name</List.Item>
        <List.Item>Australian Taxation Office details</List.Item>
        <List.Item>
          Is for End of Financial Year date within the last 18 months
        </List.Item>
        <List.Item>Shows the taxable income</List.Item>
        <List.Item>
          Is accompanied by your bank statement showing 3 months of income
        </List.Item>
      </List>
    </Stack>
  );
}

function AccountantLetter() {
  return (
    <Stack gap="2">
      <Text>
        <strong>Accountant Letter</strong>
      </Text>
      <List>
        <List.Item>Shows your full name</List.Item>
        <List.Item>
          Your accountant’s name, address, ABN/ACN and telephone number
        </List.Item>
        <List.Item>Is dated within the last 31 days</List.Item>
        <List.Item>States your income after expenses</List.Item>
        <List.Item>
          Is accompanied by your bank statement showing 3 months of income
        </List.Item>
      </List>
    </Stack>
  );
}

function TaxReturn() {
  return (
    <Stack gap="2">
      <Text>
        <strong>Tax return</strong>
      </Text>
      <List>
        <List.Item>
          Is prepared by an accountant with accountant details included
        </List.Item>
        <List.Item>Shows your full name</List.Item>
        <List.Item>
          End of Financial Year date within the last 18 months
        </List.Item>
        <List.Item>
          Includes all pages of the “accountant prepared individual Tax Return”
        </List.Item>
        <List.Item>
          Is accompanied by your bank statement showing 3 months of income
        </List.Item>
      </List>
    </Stack>
  );
}

function ProfitAndLossStatement() {
  return (
    <Stack gap="2">
      <Text>
        <strong>Profit and Loss Statement</strong>
      </Text>
      <List>
        <List.Item>
          Is prepared by an accountant with accountant details included
        </List.Item>
        <List.Item>Shows income period within the last 18 months</List.Item>
        <List.Item>Your full name</List.Item>
        <List.Item>Specifies you as a sole trader</List.Item>
        <List.Item>
          Is accompanied by your bank statement showing 3 months of income
        </List.Item>
      </List>
    </Stack>
  );
}

function CentrelinkLetter() {
  return (
    <Stack gap="2">
      <Text>
        <strong>Centrelink/Services Australia Letter or Statement</strong>
      </Text>
      <List>
        <List.Item>Shows your full name</List.Item>
        <List.Item>The Agency name</List.Item>
        <List.Item>Benefit type, amount and frequency</List.Item>
        <List.Item>Is dated within the last 31 days</List.Item>
      </List>
    </Stack>
  );
}

function CenterlinkOnline() {
  return (
    <Stack gap="2">
      <Text>
        <strong>Centrelink/Services Australia Online Payment History</strong>
      </Text>
      <List>
        <List.Item>Shows your full name</List.Item>
        <List.Item>The Agency name</List.Item>
        <List.Item>Benefit type, amount and frequency</List.Item>
        <List.Item>
          Two consecutive income payments, most recent income showing within the
          last 31 days
        </List.Item>
      </List>
    </Stack>
  );
}

function VeteransAffairs() {
  return (
    <Stack gap="2">
      <Text>
        <strong>Veterans Affairs Statement/Letter</strong>
      </Text>
      <List>
        <List.Item>Shows your full name</List.Item>
        <List.Item>Income amount and frequency</List.Item>
        <List.Item>Is dated within the last 31 days</List.Item>
        <List.Item>Is on DVA letterhead</List.Item>
      </List>
    </Stack>
  );
}

function LetterFromInsurer() {
  return (
    <Stack gap="2">
      <Text>
        <strong>Letter from Insurer</strong>
      </Text>
      <List>
        <List.Item>Shows your full name</List.Item>
        <List.Item>
          Name of insurer or relevant body administering payments
        </List.Item>
        <List.Item>Start date of payments</List.Item>
        <List.Item>
          The end date (if applicable), and the amount and frequency of payments
        </List.Item>
        <List.Item>Is dated within the last 31 days </List.Item>
      </List>
    </Stack>
  );
}

function SuperannuationStatement() {
  return (
    <Stack gap="2">
      <Text>
        <strong>Superannuation Statement</strong>
      </Text>
      <List>
        <List.Item>Shows your full name</List.Item>
        <List.Item>Superannuation provider name, ABN/ACN and address</List.Item>
        <List.Item>Issued within the last 45 days</List.Item>
        <List.Item>Shows net or gross income</List.Item>
        <List.Item>Shows benefit type, amount and frequency</List.Item>
        <List.Item>Current balance of the fund</List.Item>
      </List>
    </Stack>
  );
}

function LetterFromSuperannuation() {
  return (
    <Stack gap="2">
      <Text>
        <strong>Letter from Superannuation Fund</strong>
      </Text>
      <List>
        <List.Item>Shows your full name</List.Item>
        <List.Item>Fund name and ABN/ACN </List.Item>
        <List.Item>Issued within the last 31 days</List.Item>
        <List.Item>Shows net or gross income</List.Item>
        <List.Item>Pay frequency</List.Item>
      </List>
    </Stack>
  );
}

function RealEstateRental() {
  return (
    <Stack gap="2">
      <Text>
        <strong>Real Estate Rental Statements or Receipts</strong>
      </Text>
      <List>
        <List.Item>Shows your full name</List.Item>
        <List.Item>The last 2 months of rental income</List.Item>
        <List.Item>
          Most recent rental statement or receipt dated within the last 31 days
        </List.Item>
        <List.Item>
          Shows the Real Estate Agent’s name, ACN /ABN, address and telephone
          number
        </List.Item>
      </List>
    </Stack>
  );
}

function CurrentRentalAgreement() {
  return (
    <Stack gap="2">
      <Text>
        <strong>Current Rental Agreement (only for newly signed leases)</strong>
      </Text>
      <List>
        <List.Item>Shows your full name</List.Item>
        <List.Item>Is current, formally executed</List.Item>
        <List.Item>Shows tenant’s name</List.Item>
        <List.Item>Term of rental agreement</List.Item>
        <List.Item>Gross rental income</List.Item>
        <List.Item>Contracting parties’ signatures</List.Item>
        <List.Item>
          Accompanied with another rental income document if over 4 weeks old
        </List.Item>
      </List>
    </Stack>
  );
}
